import { HttpService } from '../shared'
import { apiRoutes } from '../shared/routing'
import { DEFAULT_QUARTERLY_SLUG } from './constants'
import { QuarterlyAccessPayload, QuarterlyAccessResponse, QuarterlyJobTitleGroup, QuarterlyMeta } from './types'

/**
 * Client for the Threat Labs Quarterly gated-download endpoints.
 * Both endpoints are guest-allowed; the content is gated server-side by the
 * successful lead POST, not by the route.
 */
export const quarterlyService = {
  /**
   * Fetch issue metadata (title/blurb/cover) plus whether the current user
   * already has access / is a known non-guest to prefill.
   */
  getMeta(slug: string): Promise<QuarterlyMeta> {
    // The slug comes straight from the public URL — encode it so it cannot
    // alter the API path (e.g. `..%2Fother-endpoint`).
    const url = apiRoutes.quarterlyMeta.replace('{slug}', encodeURIComponent(slug))
    return HttpService.get<QuarterlyMeta>(url)
  },

  /**
   * Submit the lead-capture payload and receive a short-TTL presigned URL for
   * the issue PDF.
   */
  requestAccess(slug: string, payload: QuarterlyAccessPayload): Promise<QuarterlyAccessResponse> {
    const url = apiRoutes.quarterlyAccess.replace('{slug}', encodeURIComponent(slug))
    return HttpService.post<QuarterlyAccessResponse>(url, payload)
  },

  /**
   * The admin-configured job title groups, as served with the default issue's
   * public metadata. Used outside the gate (profile professional info) to keep
   * captured job titles aligned with the curated list; resolves to an empty
   * list when unavailable so callers can fall back to free text.
   */
  async getJobTitleGroups(): Promise<QuarterlyJobTitleGroup[]> {
    try {
      const meta = await quarterlyService.getMeta(DEFAULT_QUARTERLY_SLUG)
      return meta.job_title_groups ?? []
    } catch {
      return []
    }
  },

  /**
   * Whether the default reward issue is currently published — the single-entry
   * convention the admin Reward PDF tab manages. Session-cached; used to hide
   * the appbar tab when nothing is published. Frontend-only stand-in until a
   * public latest-reward endpoint exists (STI-15836).
   */
  hasPublishedReward(): Promise<boolean> {
    if (!publishedRewardCheck) {
      publishedRewardCheck = quarterlyService
        .getMeta(DEFAULT_QUARTERLY_SLUG)
        .then(() => true)
        .catch(() => false)
    }
    return publishedRewardCheck
  },
}

let publishedRewardCheck: Promise<boolean> | undefined

/** Forget the cached published-reward state (after an admin save/remove). */
export function resetPublishedRewardCache(): void {
  publishedRewardCheck = undefined
}
